import { Tooltip } from '@civilgrid/ui/components';
import { useId } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import type { TOCChild } from '~/common/types';
import Icon, { type IconName } from '~/components2/Icon';
import layerConfigStore from '~/store2/layerConfig';
import { handleLayerIsolate, handleLayerIsolateUndo, handleLayerPinToggle } from './handlers';

interface LayerCommandsProps {
  item: TOCChild;
  isPinned: boolean;
  minZoom: number;
}

interface IconButtonProps {
  text: string;
  icon: IconName;
  onClick: () => void;
  'data-testid'?: string;
}

// `text` doubles as the tooltip copy and the accessible name, so the two always match (2.5.3).
const IconButton = ({ icon, text, onClick, 'data-testid': dataTestId }: IconButtonProps) => {
  return (
    <Tooltip asChild text={text}>
      <button
        type="button"
        onClick={onClick}
        data-testid={dataTestId}
        aria-label={text}
        className="flex cursor-pointer items-center justify-center rounded p-1 hover:bg-grey-20"
      >
        <Icon icon={icon} />
      </button>
    </Tooltip>
  );
};

export default function LayerCommands({ item, isPinned, minZoom }: LayerCommandsProps) {
  const { current: map } = useMap();
  const { isolationUndo } = useSnapshot(layerConfigStore);
  // The isolated row offers the one-level undo.
  const showUndo = isolationUndo?.leafId === item.id;
  // A bookmarked layer renders a second row with the same item.id, so the id can't derive from it.
  const isolateStatusId = useId();

  if (!map) {
    return null;
  }

  return (
    <div className="absolute right-0 hidden h-full cursor-pointer items-center space-x-1 bg-white pl-2 group-hover:flex group-focus-within:flex">
      {/* Isolate's effect is otherwise silent to a screen reader: it hides every other layer on
          the map. Always mounted, with empty text until isolated, so the live region already
          exists in the tree when there is a change to announce. */}
      <span id={isolateStatusId} className="sr-only" aria-live="polite">
        {showUndo ? `Only ${item.label} is shown` : ''}
      </span>
      {showUndo ? (
        <Tooltip asChild text="Restore previous layer visibility">
          <button
            type="button"
            data-testid={`layer-isolate-undo-${item.id}`}
            className="cursor-pointer rounded p-1 text-xs hover:bg-grey-20"
            onClick={() => handleLayerIsolateUndo(item.id)}
            aria-describedby={isolateStatusId}
          >
            Undo
          </button>
        </Tooltip>
      ) : (
        <Tooltip asChild text="Show only this layer">
          <button
            type="button"
            data-testid={`layer-isolate-${item.id}`}
            className="cursor-pointer rounded p-1 text-xs hover:bg-grey-20"
            onClick={() => handleLayerIsolate(item.id)}
          >
            Isolate
          </button>
        </Tooltip>
      )}
      {map.getZoom() < minZoom && (
        <IconButton
          icon="zoom"
          text={`Zoom to the level where ${item.label} data is available`}
          data-testid={`layer-zoom-${item.id}`}
          onClick={() => {
            if (map) {
              map.zoomTo(minZoom, {
                duration: 250,
              });
            }
          }}
        />
      )}
      <IconButton
        icon={isPinned ? 'bookmark-filled' : 'bookmark'}
        text={isPinned ? `Remove bookmark from ${item.label}` : `Bookmark ${item.label}`}
        data-testid={`layer-bookmark-toggle-${item.id}`}
        onClick={() => handleLayerPinToggle(item.id, isPinned)}
      />
    </div>
  );
}
